import React, {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import Icon from "../../btn/Icon";
import clsx from "clsx";
import VoiceWave from "./VoiceWave";
import { useMediaMd } from "../../../hooks/useMedia";
import { useAuth } from "../../../views/tutorV2/hooks/useAuth";
import { useUserQuery } from "../../../views/tutorV2/hooks/queries/useUserQuery";

interface PromptProps {
  state?: "default" | "typing" | "typing-Multiline" | "listening" | "loading";
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  onSubmit?: (value: string) => void;
  onMicClick?: () => void;
  onStopClick?: () => void;
  onCloseClick?: () => void;
  onChange?: (value: string) => void;
  className?: string;
  ariaLabel?: string;
  /** 로그인 필요 여부 (default: true) - false면 로그인 체크 및 리디렉션 비활성화 */
  requiresLogin?: boolean;
}

const Prompt: React.FC<PromptProps> = ({
  state = "default",
  value: controlledValue,
  placeholder = "무엇이든 물어보세요.",
  disabled = false,
  onSubmit,
  onMicClick,
  onStopClick,
  onCloseClick,
  onChange,
  className = "",
  ariaLabel,
  requiresLogin = true,
}) => {
  const [internalValue, setInternalValue] = useState("");
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isMd = useMediaMd();
  const { isLoggedIn } = useUserQuery();
  const { redirectToLogin } = useAuth();

  // 로그인 필요 여부에 따른 차단 상태
  const shouldBlockForLogin = requiresLogin && !isLoggedIn;

  // Auto-grow textarea height within max
  useEffect(() => {
    if (textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = "auto"; // reset
      const next = Math.min(el.scrollHeight, 160);
      el.style.height = `${next}px`;
    }
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (controlledValue === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const handleSubmit = () => {
    if (value.trim()) {
      onSubmit?.(value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Figma height rules:
  // - Mobile: listening/loading = h-[60px], others = auto
  // - Desktop: default/listening/loading = h-[64px], typing/typing-Multiline = auto
  let containerHeightClass = "";
  if (state === "listening" || state === "loading") {
    containerHeightClass = "h-fit";
  } else if (state === "default") {
    containerHeightClass = "h-fit";
  }

  return (
    <div
      className={clsx(
        "bg-[#f0eafe]",
        "flex gap-2 items-center justify-center",
        containerHeightClass,
        "md:max-w-[800px] min-h-fit",
        "px-[60px] md:px-[64px]",
        "py-[12px]",
        "relative",
        "rounded-tl-[30px] rounded-tr-[30px] md:rounded-[30px]",
        shouldBlockForLogin && "*:pointer-events-none",
        className
      )}
      aria-label={ariaLabel || "Prompt input"}
      role="form"
      onClick={shouldBlockForLogin ? redirectToLogin : undefined}
    >
      {/* Input area */}
      <div className="flex-1 relative px-0 py-2">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || state === "listening"}
          aria-label={placeholder}
          rows={1}
          className={clsx(
            "relative bottom-0 left-0 w-full bg-transparent resize-none outline-none font-medium",
            "text-sm md:text-base",
            "tracking-[-0.28px] md:tracking-[-0.32px]",
            "leading-5 md:leading-6",
            "max-h-[160px] overflow-y-auto",
            "min-h-[20px] md:min-h-[24px]",
            state === "listening" ? "invisible" : "",
            value ? "text-[#212121]" : "text-[#888888]"
          )}
        />
        {state === "listening" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <VoiceWave />
          </div>
        )}
      </div>

      {/* Left button (Mic or Close) - responsive size via Tailwind */}
      <Icon
        size={isMd ? "md" : "sm"}
        style="ghost"
        icon={state === "listening" ? "Close" : "Mic"}
        onClick={state === "listening" ? onCloseClick : onMicClick}
        className="absolute bottom-[8px] left-[8px]"
        aria-label={state === "listening" ? "Close" : "Voice input"}
      />

      {/* Right button (Arrow or Stop) - responsive size via Tailwind */}
      <Icon
        size={isMd ? "md" : "sm"}
        style="fill"
        icon={state === "loading" ? "StopWhite" : "ArrowWhite"}
        onClick={state === "loading" ? onStopClick : handleSubmit}
        className="absolute bottom-[8px] right-[8px]"
        aria-label={state === "loading" ? "Stop" : "Submit"}
        disabled={!value.trim() && state !== "loading"}
        fill="#FFFFFF"
      />
    </div>
  );
};

export default Prompt;
